/**
 * CanonicalTag — StreamX Rebrand Sprint 1 polish.
 *
 * Injects (and keeps updated) the following tags in <head> on every
 * route change:
 *
 *   • <link rel="canonical" href="https://out-play.in/streamx/...">
 *   • <meta property="og:url" ...>
 *   • <meta name="twitter:url" ...>
 *
 * Doctrine:
 *   • For `/livex/*` legacy URLs, canonical points to the equivalent
 *     `/streamx/*` URL — consolidates SEO equity onto the new brand.
 *   • For every other route the canonical points to the CURRENT path
 *     (resolves the SPA `?` parameter and ensures every page has a
 *     stable canonical).
 *   • OG / Twitter URLs always mirror the canonical so social previews
 *     point at the canonical surface.
 *   • Works in both production (out-play.in) and preview environments
 *     — the host is read from `window.location.host`.
 *   • Idempotent — re-uses existing tags rather than appending new ones.
 *
 * Mount once at the top of the app shell (App.js):
 *   <CanonicalTag />
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// ── Canonical host policy ────────────────────────────────────────────
// In production we always emit `https://out-play.in/...` so search engines
// consolidate authority on the canonical domain (even when the page is
// being served from a preview pod or staging host).  In preview/local we
// emit the current host so canonical points to a reachable URL.
function canonicalHost() {
  if (typeof window === 'undefined') return 'out-play.in';
  const host = window.location.host;
  // Treat any out-play.in domain as production canonical
  if (host === 'out-play.in' || host.endsWith('.out-play.in')) {
    return 'out-play.in';
  }
  return host;
}

function canonicalPath(pathname) {
  // Map every /livex/* URL to its /streamx/* equivalent so search engines
  // consolidate authority onto the new brand even when legacy URLs are
  // accessed directly.
  if (pathname === '/livex') return '/streamx';
  if (pathname.startsWith('/livex/')) return '/streamx/' + pathname.slice('/livex/'.length);
  // Admin namespaces follow the same rebrand
  if (pathname === '/admin/livex') return '/admin/streamx';
  if (pathname.startsWith('/admin/livex/')) return '/admin/streamx/' + pathname.slice('/admin/livex/'.length);
  if (pathname === '/super-admin/livex-waitlist') return '/super-admin/streamx-waitlist';
  return pathname;
}

function setHeadTag(selector, createAttrs, hrefOrContent, contentAttr = 'href') {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(createAttrs.tag);
    Object.entries(createAttrs.attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute(contentAttr, hrefOrContent);
}

export default function CanonicalTag() {
  const { pathname } = useLocation();

  useEffect(() => {
    const host = canonicalHost();
    const path = canonicalPath(pathname || '/');
    const canonicalUrl = `https://${host}${path}`;

    // 1. <link rel="canonical">
    setHeadTag(
      'link[rel="canonical"]',
      { tag: 'link', attrs: { rel: 'canonical' } },
      canonicalUrl,
      'href',
    );

    // 2. <meta property="og:url">
    setHeadTag(
      'meta[property="og:url"]',
      { tag: 'meta', attrs: { property: 'og:url' } },
      canonicalUrl,
      'content',
    );

    // 3. <meta name="twitter:url">
    setHeadTag(
      'meta[name="twitter:url"]',
      { tag: 'meta', attrs: { name: 'twitter:url' } },
      canonicalUrl,
      'content',
    );
  }, [pathname]);

  return null;
}
